import { Environment } from '@40seas/entities/common';
import { useAppStore } from '@40seas/frontend/dashboard/contexts';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';
import { EventTracker } from '../../components/payments-core/src/analytics/eventTrackers/EventTracker';
import { ModalAnalyticsEvent } from '../../components/payments-shared/src/lib/types/modal.events.enum';

export type DashboardEventTrackerDefaults = {
    organization: string;
    viewMode: string;
    environment: Environment;
    pageName: string;
    deviceType: 'mobile' | 'tablet' | 'desktop';
    flowId: string;
    [key: string]: string | number;
    userAgent: string;
    screenResolution: string;
    tenantId: string;
};

export class DashboardEventTracker {
    private static instance: DashboardEventTracker;
    private readonly eventTracker: EventTracker;

    private constructor() {
        this.eventTracker = EventTracker.Instance;
    }

    public static getInstance(): DashboardEventTracker {
        if (!DashboardEventTracker.instance) {
            DashboardEventTracker.instance = new DashboardEventTracker();
        }
        return DashboardEventTracker.instance;
    }

    setUser(props: { email: string; name: string }) {
        this.eventTracker.setUser(props);
    }

    private get EventsConfiguration(): DashboardEventTrackerDefaults {
        const context = useAppStore();

        return {
            organization: context.organization?.name,
            viewMode: context.viewMode,
            environment: context.isTestMode ? Environment.Sandbox : Environment.Live,
            pageName: 'Dashboard',
            deviceType: this.getDeviceType(),
            flowId: this.generateSessionId(),
            userAgent: navigator.userAgent,
            screenResolution: `${window.screen.width} x ${window.screen.height}`,
            tenantId: context.tenant as string,
        };
    }

    private generateSessionId() {
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 'a', 'b', 'c', 'd', 'e', 'f']
            .reduce<string>((acc) => `${acc}${Math.random().toString(16).substr(-4)}`, '')
            .substr(0, 36);
    }

    private getDeviceType() {
        if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            return 'mobile';
        } else if (/Tablet|iPad/i.test(navigator.userAgent)) {
            return 'tablet';
        } else {
            return 'desktop';
        }
    }

    private getEventName(args: string[], configuration = this.EventsConfiguration) {
        return [`${configuration.pageName} Page`, ...args].map((s) => startCase(camelCase(s))).join(' > ');
    }

    private enrichPayload(type: ModalAnalyticsEvent, payload: any = {}, configuration = this.EventsConfiguration) {
        return { type, ...configuration, ...payload };
    }

    private trackEvent(eventName: string, event: ModalAnalyticsEvent, payload: any, configuration = this.EventsConfiguration) {
        const ePayload = this.enrichPayload(event, payload, configuration);

        this.eventTracker.mixpanelClient.track(eventName, ePayload);
    }

    public useTrackClick(event = ModalAnalyticsEvent.CLICK) {
        const configuration = this.EventsConfiguration;

        return (buttonName: string, payload: any = {}) => {
            const eventName = this.getEventName([buttonName, event], configuration);
            this.trackEvent(eventName, event, payload, configuration);
        };
    }

    public trackViewedPage(payload: any = {}) {
        const eventName = this.getEventName([ModalAnalyticsEvent.PAGE_VIEWED]);

        return this.trackEvent(eventName, ModalAnalyticsEvent.PAGE_VIEWED, payload);
    }
}
